import { useMemo, useRef, useState } from 'react';
import {
    Box,
    Button,
    Grid,
    Paper,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TableSortLabel,
    CircularProgress,
} from '@mui/material';
import { Refresh as RefreshIcon } from '@mui/icons-material';
import axios from '../../utils/axios';
import { useNotify } from '../../hooks';
import DateRangeField from '../../components/ui/DateRangeField';
import ProductFilters, { buildFilterParams } from '../../components/ui/ProductFilters';

export default function AnalyticsSales() {
    const { error: showError } = useNotify();

    const formatYmd = (d) => {
        const y = d.getFullYear();
        const m = String(d.getMonth() + 1).padStart(2, '0');
        const day = String(d.getDate()).padStart(2, '0');
        return `${y}-${m}-${day}`;
    };

    const today = new Date();
    const minus7 = new Date(today);
    minus7.setDate(today.getDate() - 7);

    // State
    const [filters, setFilters] = useState({});
    const [dateFrom, setDateFrom] = useState(formatYmd(minus7));
    const [dateTo, setDateTo] = useState(formatYmd(today));
    const [loading, setLoading] = useState(false);

    const [subgroupRows, setSubgroupRows] = useState([]);
    const [articleRows, setArticleRows] = useState([]);

    // Cross-filter selections
    const [selPodgruppa, setSelPodgruppa] = useState(null);
    const [selArticle, setSelArticle] = useState(null);

    // Request version counters — prevent stale responses from overwriting fresh data
    const sgVersionRef = useRef(0);
    const artVersionRef = useRef(0);

    // Sorting state
    const [sgSort, setSgSort] = useState({ key: 'ordersSumRub', dir: 'desc' });
    const [artSort, setArtSort] = useState({ key: 'ordersSumRub', dir: 'desc' });

    // Styles
    const cellStyle = { py: 0.5, px: 1, fontSize: '0.8rem', whiteSpace: 'nowrap' };
    const headerStyle = { ...cellStyle, fontWeight: 'bold', bgcolor: 'grey.100' };

    const fmtNum = (v, decimals = 0) => {
        if (v == null || v === '') return '-';
        const n = Number(v);
        if (isNaN(n)) return '-';
        return n.toLocaleString('ru-RU', { minimumFractionDigits: decimals, maximumFractionDigits: decimals });
    };
    const fmtMoney = (v) => fmtNum(v, 2);
    const fmtPct = (v) => {
        if (v == null || v === '') return '-';
        return `${fmtNum(v, 2)}%`;
    };

    // Build params with optional cross-filter overrides
    const buildParams = (overrides = {}) => {
        const params = buildFilterParams(filters);
        if (dateFrom) params.date_from = dateFrom;
        if (dateTo) params.date_to = dateTo;
        if (overrides.podgruppa) params.podgruppa = overrides.podgruppa;
        if (overrides.article) params.article = overrides.article;
        return params;
    };

    // Fetch helper for individual tables (with version guard against race conditions)
    const fetchSubgroups = async (params) => {
        const version = ++sgVersionRef.current;
        const resp = await axios.get('/analytics/wb/statistic/by-subgroup', { params });
        if (version === sgVersionRef.current) {
            setSubgroupRows(resp.data.rows || []);
        }
    };
    const fetchArticles = async (params) => {
        const version = ++artVersionRef.current;
        const resp = await axios.get('/analytics/wb/statistic/by-article-agg', { params });
        if (version === artVersionRef.current) {
            setArticleRows(resp.data.rows || []);
        }
    };

    // Load data (reset selections and clear stale data)
    const load = async () => {
        setLoading(true);
        setSelPodgruppa(null);
        setSelArticle(null);
        setSubgroupRows([]);
        setArticleRows([]);
        try {
            const params = buildParams();
            await Promise.all([fetchSubgroups(params), fetchArticles(params)]);
        } catch (e) {
            showError(e?.response?.data?.message || e?.message || 'Ошибка загрузки данных');
        } finally {
            setLoading(false);
        }
    };

    // Cross-filter: click subgroup row
    const onSelectPodgruppa = async (pg) => {
        const newVal = selPodgruppa === pg ? null : pg;
        setSelPodgruppa(newVal);
        setSelArticle(null);
        setArticleRows([]);
        setLoading(true);
        try {
            const base = buildParams();
            if (newVal) {
                await Promise.all([
                    fetchSubgroups(base),
                    fetchArticles({ ...base, podgruppa: newVal }),
                ]);
            } else {
                await Promise.all([fetchSubgroups(base), fetchArticles(base)]);
            }
        } catch (e) {
            showError(e?.response?.data?.message || e?.message || 'Ошибка');
        } finally {
            setLoading(false);
        }
    };

    // Cross-filter: click article row
    const onSelectArticle = async (art) => {
        const newVal = selArticle === art ? null : art;
        setSelArticle(newVal);
        setSelPodgruppa(null);
        setSubgroupRows([]);
        setLoading(true);
        try {
            const base = buildParams();
            if (newVal) {
                await Promise.all([
                    fetchSubgroups({ ...base, article: newVal }),
                    fetchArticles(base),
                ]);
            } else {
                await Promise.all([fetchSubgroups(base), fetchArticles(base)]);
            }
        } catch (e) {
            showError(e?.response?.data?.message || e?.message || 'Ошибка');
        } finally {
            setLoading(false);
        }
    };

    // Columns definition
    const columns = useMemo(() => {
        const mp = String(filters.mp || '0');
        const showOzonSplit = !filters.mp || mp === '0' || mp === '2';
        const cols = [
            { key: 'openCardCount', label: 'Переходы', align: 'right', fmt: (v) => fmtNum(v) },
            { key: 'ordersCount', label: 'Заказы', align: 'right', fmt: (v) => fmtNum(v) },
            { key: 'ordersSumRub', label: 'Сумма заказов', align: 'right', fmt: (v) => fmtMoney(v) },
            { key: 'avgPrice', label: 'Средняя цена заказа', align: 'right', fmt: (v) => fmtMoney(v) },
            { key: 'comission', label: 'Комиссия', align: 'right', fmt: (v) => fmtMoney(v) },
            { key: 'logistic', label: 'Логистика', align: 'right', fmt: (v) => fmtMoney(v) },
            { key: 'cost', label: 'Себестоимость', align: 'right', fmt: (v) => fmtMoney(v) },
            { key: 'advSum', label: 'Реклама', align: 'right', fmt: (v) => fmtMoney(v) },
        ];
        if (showOzonSplit) {
            cols.push(
                { key: 'advCpcSum', label: 'За клик', align: 'right', fmt: (v) => fmtMoney(v) },
                { key: 'advCpoSum', label: 'За заказ', align: 'right', fmt: (v) => fmtMoney(v) },
            );
        }
        cols.push(
            { key: 'drr', label: 'ДРР', align: 'right', fmt: (v) => fmtPct(v) },
            { key: 'buyoutPercent', label: 'Выкуп', align: 'right', fmt: (v) => fmtPct(v) },
            { key: 'profit', label: 'Профит', align: 'right', fmt: (v) => fmtMoney(v) },
            { key: 'roi', label: 'ROI', align: 'right', fmt: (v) => fmtPct(v) },
        );
        return cols;
    }, [filters.mp]);

    // Sorting helper
    const sortRows = (rows, sort) => {
        const sorted = [...rows];
        sorted.sort((a, b) => {
            const va = Number(a[sort.key]) || 0;
            const vb = Number(b[sort.key]) || 0;
            return sort.dir === 'asc' ? va - vb : vb - va;
        });
        return sorted;
    };

    const sortedSubgroupRows = useMemo(() => sortRows(subgroupRows, sgSort), [subgroupRows, sgSort]);
    const sortedArticleRows = useMemo(() => sortRows(articleRows, artSort), [articleRows, artSort]);

    // Totals
    const calcTotals = (rows) => {
        const t = rows.reduce((acc, r) => {
            acc.openCardCount += Number(r.openCardCount || 0);
            acc.ordersCount += Number(r.ordersCount || 0);
            acc.ordersSumRub += Number(r.ordersSumRub || 0);
            acc.comission += Number(r.comission || 0);
            acc.logistic += Number(r.logistic || 0);
            acc.cost += Number(r.cost || 0);
            acc.advSum += Number(r.advSum || 0);
            acc.advCpcSum += Number(r.advCpcSum || 0);
            acc.advCpoSum += Number(r.advCpoSum || 0);
            acc.profit += Number(r.profit || 0);
            return acc;
        }, { openCardCount: 0, ordersCount: 0, ordersSumRub: 0, comission: 0, logistic: 0, cost: 0, advSum: 0, advCpcSum: 0, advCpoSum: 0, profit: 0 });
        t.avgPrice = t.ordersCount ? +(t.ordersSumRub / t.ordersCount).toFixed(2) : 0;
        t.drr = t.ordersSumRub ? +(100 * t.advSum / t.ordersSumRub).toFixed(2) : 0;
        t.roi = t.cost ? +(100 * t.profit / t.cost).toFixed(2) : 0;
        // Weighted average buyoutPercent
        const wSum = rows.reduce((s, r) => s + (Number(r.buyoutPercent || 0) * Number(r.ordersCount || 0)), 0);
        t.buyoutPercent = t.ordersCount ? +(wSum / t.ordersCount).toFixed(2) : 0;
        return t;
    };

    const sgTotals = useMemo(() => calcTotals(subgroupRows), [subgroupRows]);
    const artTotals = useMemo(() => calcTotals(articleRows), [articleRows]);

    const handleSort = (setter, current, key) => {
        setter({ key, dir: current.key === key && current.dir === 'desc' ? 'asc' : 'desc' });
    };

    const renderTable = (title, rows, totals, nameKey, nameLabel, sort, setSort, { extraCols = [], selectedValue = null, onSelect = null } = {}) => (
        <Paper sx={{ p: 1.5, mb: 2 }}>
            <Typography variant="subtitle1" fontWeight="bold" gutterBottom>{title}</Typography>
            <TableContainer sx={{ maxHeight: 600 }}>
                <Table size="small" stickyHeader>
                    <TableHead>
                        <TableRow>
                            <TableCell sx={headerStyle}>{nameLabel}</TableCell>
                            {extraCols.map(ec => (
                                <TableCell key={ec.key} sx={headerStyle}>{ec.label}</TableCell>
                            ))}
                            {columns.map(col => (
                                <TableCell key={col.key} sx={headerStyle} align="right">
                                    <TableSortLabel
                                        active={sort.key === col.key}
                                        direction={sort.key === col.key ? sort.dir : 'desc'}
                                        onClick={() => handleSort(setSort, sort, col.key)}
                                    >
                                        {col.label}
                                    </TableSortLabel>
                                </TableCell>
                            ))}
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {/* Totals row */}
                        {rows.length > 0 && (
                            <TableRow sx={{ bgcolor: 'grey.50' }}>
                                <TableCell sx={{ ...cellStyle, fontWeight: 'bold' }}>Итого</TableCell>
                                {extraCols.map(ec => (
                                    <TableCell key={`total-${ec.key}`} sx={{ ...cellStyle, fontWeight: 'bold' }}></TableCell>
                                ))}
                                {columns.map(col => (
                                    <TableCell key={`total-${col.key}`} sx={{ ...cellStyle, fontWeight: 'bold' }} align="right">
                                        {col.fmt(totals[col.key])}
                                    </TableCell>
                                ))}
                            </TableRow>
                        )}
                        {rows.map((row, idx) => {
                            const val = row[nameKey];
                            const isSelected = selectedValue != null && val === selectedValue;
                            return (
                                <TableRow key={val ?? idx} hover
                                    onClick={() => onSelect && onSelect(val)}
                                    sx={{
                                        cursor: onSelect ? 'pointer' : 'default',
                                        bgcolor: isSelected ? 'primary.light' : 'inherit',
                                        '&:hover': { bgcolor: isSelected ? 'primary.light' : undefined },
                                    }}>
                                    <TableCell sx={{ ...cellStyle, fontWeight: isSelected ? 'bold' : 'normal', color: isSelected ? 'primary.contrastText' : 'inherit' }}>{val || '-'}</TableCell>
                                    {extraCols.map(ec => (
                                        <TableCell key={ec.key} sx={{ ...cellStyle, color: isSelected ? 'primary.contrastText' : 'inherit' }}>{row[ec.key] || '-'}</TableCell>
                                    ))}
                                    {columns.map(col => (
                                        <TableCell key={col.key} sx={{
                                            ...cellStyle,
                                            color: isSelected ? 'primary.contrastText' : (col.key === 'profit' ? (Number(row[col.key]) >= 0 ? 'success.main' : 'error.main') : 'inherit'),
                                            fontWeight: isSelected ? 'bold' : 'normal',
                                        }} align="right">
                                            {col.fmt(row[col.key])}
                                        </TableCell>
                                    ))}
                                </TableRow>
                            );
                        })}
                        {rows.length === 0 && !loading && (
                            <TableRow>
                                <TableCell colSpan={columns.length + 1 + extraCols.length} sx={cellStyle} align="center">Нет данных</TableCell>
                            </TableRow>
                        )}
                    </TableBody>
                </Table>
            </TableContainer>
        </Paper>
    );

    return (
        <Box>
            <Typography variant="h4" fontWeight="bold" gutterBottom>
                По артикулам
            </Typography>

            {/* Filters */}
            <Paper sx={{ p: 2, mb: 2 }}>
                <ProductFilters value={filters} onChange={setFilters} />
                <Grid container spacing={2} alignItems="center" sx={{ mt: 1 }}>
                    <Grid item xs={12} sm={8} md={3.5}>
                        <DateRangeField
                            valueFrom={dateFrom} valueTo={dateTo}
                            onChange={({ from, to }) => { setDateFrom(from); setDateTo(to); }}
                        />
                    </Grid>
                </Grid>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mt: 2 }}>
                    <Button variant="contained" onClick={load} disabled={loading}
                        startIcon={loading ? <CircularProgress size={20} /> : <RefreshIcon />}>
                        Загрузить
                    </Button>
                </Box>
            </Paper>

            {/* Table: by subgroup */}
            {renderTable('Аналитика по подгруппам', sortedSubgroupRows, sgTotals, 'podgruppa', 'Подгруппа', sgSort, setSgSort, {
                selectedValue: selPodgruppa,
                onSelect: onSelectPodgruppa,
            })}

            {/* Table: by article */}
            {renderTable('Аналитика по артикулам', sortedArticleRows, artTotals, 'article_number', 'Артикул', artSort, setArtSort, {
                extraCols: [{ key: 'podgruppa', label: 'Подгруппа' }],
                selectedValue: selArticle,
                onSelect: onSelectArticle,
            })}
        </Box>
    );
}
